/**
 * REF-1 — the Component Split card's real read path (GET /sales/live/v2/refund-component-split).
 *
 * The endpoint returns a flat array of { component, amount, feeComponent } rather than the
 * FE's own `RefundComponents` vector, so this hook is the one place that gap is bridged —
 * same convention as `refundLogRow.ts` for the log itself.
 */
import { useEffect, useMemo } from 'react';
import { useBranch } from '../../../context/BranchContext';
import { useAuth } from '../../../context/AuthContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchRefundComponentSplitRequest } from '../../../redux/reports/clickHouse/clickHouseActions';
import type { OrderSource } from '../../../redux/reports/clickHouse/clickHouseApi';
import type { RefundComponentSplitApiRow } from '../../../interface/clickHouseInterface';
import { emptyComponents, type RefundComponents, type RefundSalesSource } from './refundModel';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const toOrderSource = (source: RefundSalesSource): OrderSource | undefined =>
    source === 'direct' ? 'direct' : source === 'thirdparty' ? 'thirdparty' : undefined;

/** `component` string → the RefundComponents field it posts to. */
const COMPONENT_FIELD: Record<string, keyof RefundComponents> = {
    item: 'item',
    tax: 'tax',
    tip: 'tip',
    gratuity: 'gratuity',
    'convenience fee': 'convenienceFee',
    'delivery fee': 'deliveryFee',
    'card processing fee': 'cardProcessingFee',
    'service charge': 'serviceCharge',
};

function toComponents(rows: RefundComponentSplitApiRow[]): RefundComponents {
    const c = emptyComponents();
    for (const row of rows ?? []) {
        const field = COMPONENT_FIELD[String(row.component ?? '').trim().toLowerCase()];
        if (field) c[field] = Number(row.amount) || 0;
    }
    return c;
}

export interface UseRefundComponentSplitArgs {
    startDate?: string;
    endDate?: string;
    /** Defaults to 'overall'. */
    source?: RefundSalesSource;
}

export interface UseRefundComponentSplitResult {
    components: RefundComponents;
    total: number;
    /** Only 'Item' reduces Net Sales — matches `RefundPeriodSummary.itemTotal`. */
    itemTotal: number;
    emptyState: boolean;
    loading: boolean;
    failure: boolean;
    retry: () => void;
}

export function useRefundComponentSplit({ startDate, endDate, source = 'overall' }: UseRefundComponentSplitArgs): UseRefundComponentSplitResult {
    const dispatch = useAppDispatch();
    const { selectedBranch: branch } = useBranch();
    const { auth } = useAuth();
    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const orderSource = toOrderSource(source);

    const fetch = () => {
        // Same guard as the other REF-2/REF-6 real-data hooks — locationId and token
        // resolve independently on mount; dispatching before both are ready double-hits it.
        if (!locationId || !token) return;
        dispatch(fetchRefundComponentSplitRequest(locationId, startDate, endDate, orderSource));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetch(); }, [dispatch, locationId, startDate, endDate, orderSource, token]);

    const raw = useAppSelector((s) => s.clickHouse.refundComponentSplitSuccess);
    const loading = useAppSelector((s) => s.clickHouse.refundComponentSplitLoading);
    const failure = useAppSelector((s) => s.clickHouse.refundComponentSplitFailure);

    const components = useMemo(() => toComponents(raw?.components ?? []), [raw]);
    const total = raw?.total ?? 0;
    const emptyState = raw?.emptyState ?? total <= 0;

    return { components, total, itemTotal: components.item, emptyState, loading, failure, retry: fetch };
}
